// Copyright (c) Docugami, Inc. All rights reserved.
/*eslint-disable */

import { DgXmlNames } from "./DgXmlNames";
import { DOMParser } from '@xmldom/xmldom'
import { IDgWordCanvasProxyCallbacks } from "./worker/worker";
import { addInsertOrReplaceMetadataWithSpanIdToParagraph } from './DgParagraphMetadataUtil';
import { DgWordCanvas } from "./DgWordCanvas";
import { Logger, LoggerCategory } from "utils/Logger";

// Note: the following const have eslint-disable because they form the whole of the Open-Xml-Sdk for JavaScript API.
// Most modules will use only a subset of the API, but disabling portions of the API leads to friction when writing
// code.  Disabling eslint for these variables removes the error indicating that a variable is assigned a value but
// never used.  If we ever re-write these modules in TypeScript, then the public API will change significantly.

const Enumerable = require("../../../../../utils/linq");
const Ltxml = require('../../../../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const openXml = require('../../../../../utils/openxml');

const XAttribute = Ltxml.XAttribute;
const XCData = Ltxml.XCData;
const XComment = Ltxml.XComment;
const XContainer = Ltxml.XContainer;
const XDeclaration = Ltxml.XDeclaration;
const XDocument = Ltxml.XDocument;
const XElement = Ltxml.XElement;
const XName = Ltxml.XName;
const XNamespace = Ltxml.XNamespace;
const XNode = Ltxml.XNode;
const XObject = Ltxml.XObject;
const XProcessingInstruction = Ltxml.XProcessingInstruction;
const XText = Ltxml.XText;
const XEntity = Ltxml.XEntity;
const cast = Ltxml.cast;
const castInt = Ltxml.castInt;

const W = openXml.W;
const W14 = openXml.W14;
const W15 = openXml.W15;
const NN = openXml.NoNamespace;
const wNs = openXml.wNs;
const groupAdjacent = openXml.groupAdjacent;
/*eslint-enable */

export async function dgGenerateFormattedContent(chunk: any, unselectedFirstParagraphText: string, unselectedLastParagraphText: string, guidToUse: string|null, insertOrReplaceId: string, insertElseReplace: boolean,
    paragraphToMatchFormatting: any, dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<any>
{
    const headerChunk = chunk.elements(DgXmlNames.chunk).firstOrDefault((hc: any) => hc.attribute('header_features') !== null);
    if (!!headerChunk && headerChunk.elementsAfterSelf(DgXmlNames.chunk).count() === 1) {
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, `dgGenerateFormattedContent - generateHeaderNonHeader`);
        const nonHeaderChunk = headerChunk.elementsAfterSelf(DgXmlNames.chunk).first();

        headerChunk.addFirst(new XText(unselectedFirstParagraphText));
        nonHeaderChunk.add(new XText(unselectedLastParagraphText));

        // have header/nonHeader
        const newHeaderChunkPara = await generateHeaderNonHeader(headerChunk, nonHeaderChunk, guidToUse, insertOrReplaceId, insertElseReplace, paragraphToMatchFormatting, dgWordCanvas, proxyCallbacks);

        const newDataHeaderChunkCase = [ newHeaderChunkPara ];
        return newDataHeaderChunkCase;
    }
    else {
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, `dgGenerateFormattedContent - generateDefault`);

        chunk.addFirst(new XText(unselectedFirstParagraphText));
        chunk.add(new XText(unselectedLastParagraphText));

        const newDefaultPara = await generateDefault(chunk, guidToUse, insertOrReplaceId, insertElseReplace, paragraphToMatchFormatting, dgWordCanvas, proxyCallbacks);

        const newData = [ newDefaultPara ];
        return newData;
    }
}

async function generateDefault(chunk: any, guidToUse: string|null, insertOrReplaceId: string, insertElseReplace: boolean, paragraphToMatchFormatting: any, dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<any>
{
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `generateDefault - entry`);

    const pPr = paragraphToMatchFormatting.element(W.pPr);

    const run = new XElement(W.r,
        dgWordCanvas.defaultRunProperties,
        new XElement(W.t,
            new XAttribute(XNamespace.xml + "space", "preserve"),
            normalizeText(stripListItem(getElementTextValue(chunk)))));

    const newPara = new XElement(W.p,
        pPr,
        run
    );
    await addInsertOrReplaceMetadataWithSpanIdToParagraph(newPara, guidToUse, insertOrReplaceId, insertElseReplace, proxyCallbacks);

    return newPara;
}

async function generateWithNoFormatting(contentValue: string, guidToUse: string|null, insertOrReplaceId: string, insertElseReplace: boolean, dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<any>
{
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `generateWithNoFormatting - entry`);

    const run = new XElement(W.r,
        dgWordCanvas.defaultRunProperties,
        new XElement(W.t,
            new XAttribute(XNamespace.xml + "space", "preserve"),
            normalizeText(contentValue)));

    const newPara = new XElement(W.p,
       run
    );
    await addInsertOrReplaceMetadataWithSpanIdToParagraph(newPara, guidToUse, insertOrReplaceId, insertElseReplace, proxyCallbacks);
    return newPara;
}

export function getElementTextValue(element: any)
{
    var returnValue = element
        .descendantNodes()
        .where(function (n: any) {
            return n.nodeType === 'Text' ||
            n.nodeType === 'CDATA' ||
            n.nodeType === 'Entity';
        })
        .select(function (n: any) {
            if (n.nodeType === 'Text' || n.nodeType === 'CDATA')
            {
                return n.value;
            }
            else // Entity
            {
                if (n.value === "apos")
                {
                    return '’';
                }
                return n.value;
            }
        })
        .toArray()
        .join('');
    return returnValue;
}

async function generateHeaderNonHeader(headerChunk: any, nonHeaderChunk: any, guidToUse: string|null, spanId: string, insertElseReplace: boolean, paragraphToMatchFormatting: any, dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks): Promise<any>
{
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `generateHeaderNonHeader - entry`);
    const fal: FormattingAtom[] = [];
    createFormattingAtomListRecurse(paragraphToMatchFormatting, fal);

    // if paragraph to match formatting contains no content in it (nothing to match)
    if (fal.length === 0)
    {
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, `generateHeaderNonHeader - generate with no formatting`);
        const content = getElementTextValue(headerChunk) + getElementTextValue(nonHeaderChunk);
        const newPara = await generateWithNoFormatting(content, guidToUse, spanId, insertElseReplace, dgWordCanvas, proxyCallbacks);
        return newPara;
    }

    const projectedWithFormatting = projectWithFormatting(fal);
    const grouped = groupAdjacent(projectedWithFormatting, (t: any) => t.runProperties);

    let firstRunRpr: any = null;
    const foundFirstRunProjected = grouped[0].Group.firstOrDefault();
    if (!!foundFirstRunProjected) {
        firstRunRpr = foundFirstRunProjected.rPr;
        if (!!firstRunRpr)
        {
            firstRunRpr = new XElement(firstRunRpr);
            const spacingElement = firstRunRpr.element(W.spacing);
            if (spacingElement)
            {
                spacingElement.remove();
            }
        }
    }

    const pPr = paragraphToMatchFormatting.element(W.pPr);

    // if there are spaces at the end of firstRunContent, move them to the beginning of secondRunContent
    let firstRunContent = stripListItem(getElementTextValue(headerChunk));
    let secondRunContent = getElementTextValue(nonHeaderChunk);
    const firstRunContentTrimEnd = openXml.util.trimEnd(firstRunContent, null);
    if (firstRunContent.length !== firstRunContentTrimEnd.length)
    {
        firstRunContent = firstRunContentTrimEnd;
        secondRunContent = " " + secondRunContent;
    }

    const firstRun = new XElement(W.r,
        firstRunRpr,
        new XElement(W.t,
            new XAttribute(XNamespace.xml + "space", "preserve"),
            normalizeText(firstRunContent)));

    const secondRun = new XElement(W.r,
        dgWordCanvas.defaultRunProperties,
        new XElement(W.t,
            new XAttribute(XNamespace.xml + "space", "preserve"),
            normalizeText(secondRunContent)));

    const newPara = new XElement(W.p,
        pPr,
        firstRun,
        secondRun
    );
    addInsertOrReplaceMetadataWithSpanIdToParagraph(newPara, guidToUse, spanId, insertElseReplace, proxyCallbacks);
    return newPara;
}

function projectWithFormatting(fal: FormattingAtom[]): any
{
    const projectedWithFormatting = Enumerable.from(fal)
        .select((at: any) =>
        {
            let rPrStr = '';
            let rPr: any = null;
            const runAncestor = at.contentElement.ancestorsAndSelf(W.r).firstOrDefault();
            if (runAncestor !== null)
            {
                rPr = runAncestor.element(W.rPr);
                if (rPr !== null) {
                    const cloned = new XElement(rPr);
                    const caps = cloned.element(W.caps);
                    if (caps !== null) {
                        caps.remove();
                    }
                    rPrStr = cloned.toString(false); // don't indent
                }
            }
            const projectedValue = {
                character: at.contentCharacter,
                contentElement: at.contentElement,
                runProperties: rPrStr,
                rPr: rPr,
            };
            return projectedValue;
        })
        .toArray();
    return projectedWithFormatting;
}

function normalizeText(text: string): string
{
    let text2 = text
        .replace(/\s+/g, ' ')
        .replace(/\s[,]/g, ',')
        .replace(/\s[.]/g, '.')
        .replace(/\s[)]/g, ')')
        .replace(/[(]\s/g, '(')
        .replace(/[‘]\s/g, '‘')
        .replace(/[’]\s/g, '’')
        .replace(/^●\s/g, '');
    return text2;
}

export function stripListItem(text: string): string
{
    // openXml is the entire document, so that can look at numbering part
    // numPr is the numbering spec, so that can properly strip the necessary list item from the DGML content.
    // note: this is for future implementation.
    // currently, this is a 'hack' placeholder until we can properly implement this functionality.

    const text1 = openXml.util.trimStart(text, null);
    let text2 = text1.replace(/^[\u2022\uf0b7\uf0a7\uf076\uf0d8\uf0fc]\s+/g, '');
    if (text2 !== text1) {
        return text2;
    }
    text2 = text1.replace(/^\d.\s+/g, '');
    if (text2 !== text1) {
        return text2;
    }
    text2 = text1.replace(/^[A-Za-z].\s+/g, '');
    if (text2 !== text1) {
        return text2;
    }
    text2 = text1.replace(/^[0-9]+.\s+/g, '');
    if (text2 !== text1) {
        return text2;
    }
    text2 = text1.replace(/^\([iv]+\)\s+/g, '');
    if (text2 !== text1) {
        return text2;
    }
    text2 = text1.replace(/^\([A-Za-z]+\)\s+/g, '');
    if (text2 !== text1) {
        return text2;
    }
    return text1;
}

class FormattingAtom {
    public contentCharacter: string;
    public contentElement: any;
    constructor(contentElement: any, contentCharacter: string)
    {
        this.contentCharacter = contentCharacter;
        this.contentElement = contentElement;
    }
}

function createFormattingAtomListRecurse(node:any, fal: FormattingAtom[])
{
    if (node.nodeType === 'Element' && node.name === W.instrText) {
        return;
    }

    if (node.nodes) {
        if (node.nodes().any((n: any) => (n.nodeType === 'Text' || n.nodeType === 'Entity'))) {
            const childNodes = node.nodes().toArray();
            for (let i = 0; i < childNodes.length; ++i)
            {
                if (childNodes[i].nodeType === 'Text') {
                    const val = childNodes[i].value;
                    for (let j = 0; j < val.length; j++) {
                        const ch = val.charAt(j);
                        if (ch !== '\n') {
                            const fa = new FormattingAtom(childNodes[i].parent, ch);
                            fal.push(fa);
                        }
                    }
                }
                else if (childNodes[i].nodeType === 'Entity') {
                    const ent = childNodes[i];
                    const toString = ent.toString();
                    const doc = new DOMParser().parseFromString("<p>" + toString + "</p", "text/html");
                    const ch = doc.documentElement.textContent as string;
                    const fa = new FormattingAtom(childNodes[i].parent, ch);
                    fal.push(fa);
                }
                else {
                    createFormattingAtomListRecurse(childNodes[i], fal);
                }
            }
            return;
        }
    }
    if (node.nodeType === 'Element') {
        const nodeArray = node.nodes().toArray();
        for (let idx = 0; idx < nodeArray.length; ++idx)
        {
            createFormattingAtomListRecurse(nodeArray[idx], fal);
        }
    }
};
